import { FormEventHandler, ReactElement, useState } from 'react';
import { Device } from '@/api';
import {
    UIFormInput,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISwitch,
    UIText,
    UITextButton
} from '@/ui';
import { useUpdateDeviceTiltAlertAction } from '../actions';
import { useDevicesTranslation } from '../hooks';
import { PendingConfigNote } from './pending-config-note';

const DEFAULT_ANGLE = '45';
const DEFAULT_TIME_THRESHOLD = '180';

export type TiltAlertModalProps = {
    device: Device;
    isOpen: boolean;
    onClose: () => void;
};

export function TiltAlertModal(props: TiltAlertModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: TiltAlertModalProps): ReactElement {
    const { device, onClose } = props;
    const { t } = useDevicesTranslation();
    const { updateDeviceTiltAlert, loading } = useUpdateDeviceTiltAlertAction({
        deviceId: device.id
    });

    const tilt = device.config?.tiltAlert;
    const [enabled, setEnabled] = useState(tilt?.enabled ?? false);
    const [angle, setAngle] = useState(String(tilt?.angle ?? DEFAULT_ANGLE));
    const [timeThreshold, setTimeThreshold] = useState(
        String(tilt?.timeThreshold ?? DEFAULT_TIME_THRESHOLD)
    );
    const [showErrors, setShowErrors] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    function angleError(): string {
        const value = Number(angle);
        if (!Number.isInteger(value) || value < 30 || value > 90) {
            return t('shared.fields.tilt_angle.error_invalid');
        }
        return '';
    }

    function timeError(): string {
        const value = Number(timeThreshold);
        if (!Number.isInteger(value) || value < 60 || value > 180) {
            return t('shared.fields.tilt_time.error_invalid');
        }
        return '';
    }

    function handleResetDefaults() {
        setAngle(DEFAULT_ANGLE);
        setTimeThreshold(DEFAULT_TIME_THRESHOLD);
        setShowErrors(false);
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setShowErrors(true);

        if (enabled && (angleError() || timeError())) {
            setShowAdvanced(true);
            return;
        }

        // No change from the current config — don't queue a pointless command.
        const unchanged =
            enabled === (tilt?.enabled ?? false) &&
            (!enabled ||
                (Number(angle) === tilt?.angle &&
                    Number(timeThreshold) === tilt?.timeThreshold));
        if (unchanged) {
            onClose();
            return;
        }

        updateDeviceTiltAlert(
            enabled
                ? {
                      enabled: true,
                      angle: Number(angle),
                      timeThreshold: Number(timeThreshold)
                  }
                : { enabled: false }
        )
            .then(() => onClose())
            .catch(() => {}); // Error handled in the action
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader title={t('components.tilt_alert_modal.title')} />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <PendingConfigNote />

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>{t('shared.devices.tilt_label')}</UIText>
                            <UIText type="default2">
                                {t(
                                    'components.tilt_alert_modal.enabled_description'
                                )}
                            </UIText>
                        </div>
                        <UISwitch checked={enabled} onChange={setEnabled} />
                    </div>

                    {enabled && (
                        <>
                            <div style={{ alignSelf: 'flex-start' }}>
                                <UITextButton
                                    type="button"
                                    variant="primary"
                                    leftIcon={
                                        showAdvanced
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    }
                                    onClick={() =>
                                        setShowAdvanced((show) => !show)
                                    }
                                >
                                    {t('shared.labels.advanced_settings')}
                                </UITextButton>
                            </div>
                            {showAdvanced && (
                                <>
                                    <UIFormInput
                                        label={t(
                                            'shared.devices.tilt_angle_label'
                                        )}
                                        description={t(
                                            'shared.devices.tilt_angle_description'
                                        )}
                                        type="number"
                                        value={angle}
                                        onChange={setAngle}
                                        errorText={
                                            showErrors ? angleError() : ''
                                        }
                                    />
                                    <UIFormInput
                                        label={t(
                                            'shared.devices.tilt_time_label'
                                        )}
                                        description={t(
                                            'shared.devices.tilt_time_description'
                                        )}
                                        type="number"
                                        value={timeThreshold}
                                        onChange={setTimeThreshold}
                                        errorText={
                                            showErrors ? timeError() : ''
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('shared.labels.submit')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onClose
                    },
                    ...(enabled && showAdvanced
                        ? [
                              {
                                  variant: 'textDefault' as const,
                                  type: 'button' as const,
                                  text: t('shared.labels.revert_default'),
                                  disabled: loading,
                                  onClick: handleResetDefaults
                              }
                          ]
                        : [])
                ]}
            />
        </form>
    );
}
